import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { formatCurrency } from '../../utils/currencyFormatter';

// Helper function to filter data by date range
const filterByDateRange = (data, dateRange, dateField) => {
  if (!dateRange.start || !dateRange.end) return data;
  
  const startDate = startOfDay(parseISO(dateRange.start));
  const endDate = endOfDay(parseISO(dateRange.end));
  
  return data.filter(item => {
    const itemDate = item[dateField];
    if (!itemDate) return false;
    
    let date;
    // Handle Firestore Timestamp, Date object, or string
    if (typeof itemDate === 'object' && itemDate !== null) {
      // Firestore Timestamp (has toDate method)
      if (typeof itemDate.toDate === 'function') {
        date = itemDate.toDate();
      } else if (itemDate instanceof Date) {
        date = itemDate;
      } else {
        // Try to parse as ISO string if possible
        date = new Date(itemDate);
      }
    } else if (typeof itemDate === 'string') {
      date = parseISO(itemDate);
    } else {
      date = new Date(itemDate);
    }
    if (isNaN(date)) return false;
    return isWithinInterval(date, { start: startDate, end: endDate });
  });
};

// Helper to safely convert any date value to a JS Date or null
function safeToDate(val) {
  if (!val) return null;
  if (typeof val === 'object' && val !== null) {
    if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
    if (val instanceof Date) return isNaN(val) ? null : val;
    // Try to parse as ISO string
    const d = new Date(val);
    return isNaN(d) ? null : d;
  }
  if (typeof val === 'string') {
    const d = new Date(val);
    return isNaN(d) ? null : d;
  }
  // Try as timestamp/number
  const d = new Date(val);
  return isNaN(d) ? null : d;
}

// Update the footer helper to include user info and timestamp
function addPageFooter(doc, user, generatedAt) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(9);
    let userLine = '';
    if (user) {
      userLine = `User: ${(user.fullName || user.username || user.email || 'N/A')} | Email: ${(user.email || 'N/A')} | Role: ${(user.role || 'N/A')}`;
    }
    const timestampLine = `Generated: ${generatedAt}`;
    const pageLine = `Page ${i} of ${pageCount}`;
    // Compose footer: user info, timestamp, page number
    const footer = `${userLine}\n${timestampLine}\n${pageLine}`;
    // Draw each line
    const lines = footer.split('\n');
    lines.forEach((line, idx) => {
      doc.text(line, pageWidth / 2, pageHeight - 14 + idx * 5, { align: 'center' });
    });
  }
}

// Generate Earnings Report
export const generateEarningsReport = async (payments, dateRange, user) => {
  const filteredPayments = filterByDateRange(payments, dateRange, 'paymentDate');
  
  const doc = new jsPDF();
  const generatedAt = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
  
  // Header
  doc.setFontSize(20);
  doc.text('Earnings Report', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Period: ${dateRange.start} to ${dateRange.end} | Generated: ${generatedAt}`, 105, 30, { align: 'center' });
  
  // Summary
  const totalRevenue = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const paidPayments = filteredPayments.filter(p => p.status === 'Paid');
  const pendingPayments = filteredPayments.filter(p => p.status === 'Pending');
  const overduePayments = filteredPayments.filter(p => p.status === 'Overdue');
  
  doc.setFontSize(14);
  doc.text('Summary', 20, 60);
  
  doc.setFontSize(10);
  doc.text(`Total Revenue: ${formatCurrency(totalRevenue)}`, 20, 70);
  doc.text(`Paid Payments: ${paidPayments.length}`, 20, 80);
  doc.text(`Pending Payments: ${pendingPayments.length}`, 20, 90);
  doc.text(`Overdue Payments: ${overduePayments.length}`, 20, 100);
  
  // Payment Details Table
  const tableData = filteredPayments.map(payment => [
    payment.tenantName || 'N/A',
    payment.apartmentNumber || 'N/A',
    formatCurrency(payment.amount || 0),
    payment.paymentType || 'N/A',
    payment.status || 'N/A',
    (() => { const d = safeToDate(payment.paymentDate); return d ? format(d, 'MM/dd/yyyy') : 'N/A'; })()
  ]);
  
  doc.autoTable({
    startY: 120,
    head: [['Tenant', 'Apartment', 'Amount', 'Type', 'Status', 'Date']],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [66, 139, 202] },
    styles: { fontSize: 8 },
    columnStyles: {
      2: { halign: 'right' } // Amount column right-aligned
    }
  });
  
  addPageFooter(doc, user, generatedAt);
  
  // Save the PDF
  const fileName = `earnings-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fileName);
  
  return { success: true, fileName };
};

// Generate Occupancy Report
export const generateOccupancyReport = async (apartments, users, dateRange, user) => {
  const doc = new jsPDF();
  const generatedAt = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
  
  // Header
  doc.setFontSize(20);
  doc.text('Occupancy Report', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  
  // Summary
  const totalApartments = apartments.length;
  const occupiedApartments = apartments.filter(apt => apt.status === 'Occupied');
  const availableApartments = apartments.filter(apt => apt.status === 'Available');
  const maintenanceApartments = apartments.filter(apt => apt.status === 'Under Maintenance');
  const occupancyRate = totalApartments > 0 ? (occupiedApartments.length / totalApartments) * 100 : 0;
  
  doc.setFontSize(14);
  doc.text('Summary', 20, 50);
  
  doc.setFontSize(10);
  doc.text(`Total Apartments: ${totalApartments}`, 20, 60);
  doc.text(`Occupied: ${occupiedApartments.length}`, 20, 70);
  doc.text(`Available: ${availableApartments.length}`, 20, 80);
  doc.text(`Under Maintenance: ${maintenanceApartments.length}`, 20, 90);
  doc.text(`Occupancy Rate: ${occupancyRate.toFixed(1)}%`, 20, 100);
  
  // Apartment Details Table
  const tableData = apartments.map(apartment => [
    apartment.apartmentNumber || 'N/A',
    apartment.building || 'N/A',
    apartment.floor || 'N/A',
    apartment.bedrooms || 'N/A',
    apartment.bathrooms || 'N/A',
    formatCurrency(apartment.monthlyRent || 0),
    apartment.status || 'N/A'
  ]);
  
  doc.autoTable({
    startY: 120,
    head: [['Unit', 'Building', 'Floor', 'Bedrooms', 'Bathrooms', 'Monthly Rent', 'Status']],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [66, 139, 202] },
    styles: { fontSize: 8 },
    columnStyles: {
      5: { halign: 'right' } // Rent column right-aligned
    }
  });
  
  // Tenant Information
  const tenants = users.filter(user => user.role === 'tenant');
  const tenantTableData = tenants.map(tenant => [
    tenant.fullName || tenant.username || 'N/A',
    tenant.email || 'N/A',
    tenant.phone || 'N/A',
    tenant.apartmentId || 'N/A'
  ]);
  
  if (tenantTableData.length > 0) {
    doc.addPage();
    doc.setFontSize(16);
    doc.text('Tenant Information', 20, 20);
    
    doc.autoTable({
      startY: 30,
      head: [['Name', 'Email', 'Phone', 'Apartment ID']],
      body: tenantTableData,
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] },
      styles: { fontSize: 8 }
    });
  }
  
  addPageFooter(doc, user, generatedAt);
  
  // Save the PDF
  const fileName = `occupancy-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fileName);
  
  return { success: true, fileName };
};

// Generate Maintenance Report
export const generateMaintenanceReport = async (maintenance, dateRange, user) => {
  const filteredMaintenance = filterByDateRange(maintenance, dateRange, 'requestDate');
  
  const doc = new jsPDF();
  const generatedAt = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
  
  // Header
  doc.setFontSize(20);
  doc.text('Maintenance Report', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Period: ${dateRange.start} to ${dateRange.end} | Generated: ${generatedAt}`, 105, 30, { align: 'center' });
  
  // Summary
  const totalRequests = filteredMaintenance.length;
  const pendingRequests = filteredMaintenance.filter(m => m.status === 'Pending');
  const inProgressRequests = filteredMaintenance.filter(m => m.status === 'In Progress');
  const completedRequests = filteredMaintenance.filter(m => m.status === 'Completed');
  const emergencyRequests = filteredMaintenance.filter(m => m.priority === 'Emergency');
  
  doc.setFontSize(14);
  doc.text('Summary', 20, 60);
  
  doc.setFontSize(10);
  doc.text(`Total Requests: ${totalRequests}`, 20, 70);
  doc.text(`Pending: ${pendingRequests.length}`, 20, 80);
  doc.text(`In Progress: ${inProgressRequests.length}`, 20, 90);
  doc.text(`Completed: ${completedRequests.length}`, 20, 100);
  doc.text(`Emergency Requests: ${emergencyRequests.length}`, 20, 110);
  
  // Maintenance Details Table
  const tableData = filteredMaintenance.map(request => [
    request.apartmentNumber || 'N/A',
    request.issueType || 'N/A',
    request.priority || 'N/A',
    request.status || 'N/A',
    (() => { const d = safeToDate(request.requestDate); return d ? format(d, 'MM/dd/yyyy') : 'N/A'; })(),
    request.assignedTo || 'Unassigned'
  ]);
  
  doc.autoTable({
    startY: 130,
    head: [['Apartment', 'Issue Type', 'Priority', 'Status', 'Request Date', 'Assigned To']],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [66, 139, 202] },
    styles: { fontSize: 8 }
  });
  
  // Priority Breakdown
  const priorityBreakdown = {};
  filteredMaintenance.forEach(request => {
    const priority = request.priority || 'Unknown';
    priorityBreakdown[priority] = (priorityBreakdown[priority] || 0) + 1;
  });
  
  if (Object.keys(priorityBreakdown).length > 0) {
    doc.addPage();
    doc.setFontSize(16);
    doc.text('Priority Breakdown', 20, 20);
    
    const priorityData = Object.entries(priorityBreakdown).map(([priority, count]) => [
      priority,
      count.toString()
    ]);
    
    doc.autoTable({
      startY: 30,
      head: [['Priority', 'Count']],
      body: priorityData,
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] },
      styles: { fontSize: 10 }
    });
  }
  
  addPageFooter(doc, user, generatedAt);
  
  // Save the PDF
  const fileName = `maintenance-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fileName);
  
  return { success: true, fileName };
};

// Generate Comprehensive Financial Report
export const generateFinancialReport = async (payments, apartments, dateRange, user) => {
  const filteredPayments = filterByDateRange(payments, dateRange, 'paymentDate');
  
  const doc = new jsPDF();
  const generatedAt = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
  
  // Header
  doc.setFontSize(20);
  doc.text('Financial Report', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Period: ${dateRange.start} to ${dateRange.end} | Generated: ${generatedAt}`, 105, 30, { align: 'center' });
  
  // Financial Summary
  const totalRevenue = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const rentPayments = filteredPayments.filter(p => p.paymentType === 'Rent');
  const depositPayments = filteredPayments.filter(p => p.paymentType === 'Deposit');
  const lateFeePayments = filteredPayments.filter(p => p.paymentType === 'Late Fee');
  const maintenanceFeePayments = filteredPayments.filter(p => p.paymentType === 'Maintenance Fee');
  
  const rentRevenue = rentPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const depositRevenue = depositPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const lateFeeRevenue = lateFeePayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const maintenanceFeeRevenue = maintenanceFeePayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  
  doc.setFontSize(14);
  doc.text('Financial Summary', 20, 60);
  
  doc.setFontSize(10);
  doc.text(`Total Revenue: ${formatCurrency(totalRevenue)}`, 20, 70);
  doc.text(`Rent Revenue: ${formatCurrency(rentRevenue)}`, 20, 80);
  doc.text(`Deposit Revenue: ${formatCurrency(depositRevenue)}`, 20, 90);
  doc.text(`Late Fee Revenue: ${formatCurrency(lateFeeRevenue)}`, 20, 100);
  doc.text(`Maintenance Fee Revenue: ${formatCurrency(maintenanceFeeRevenue)}`, 20, 110);
  
  // Monthly Breakdown
  const monthlyData = {};
  filteredPayments.forEach(payment => {
    if (payment.paymentDate) {
      const date = new Date(payment.paymentDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { name: monthName, amount: 0, count: 0 };
      }
      monthlyData[monthKey].amount += payment.amount || 0;
      monthlyData[monthKey].count += 1;
    }
  });
  
  const monthlyTableData = Object.values(monthlyData)
    .sort((a, b) => {
      const dateA = new Date(a.name);
      const dateB = new Date(b.name);
      return dateA - dateB;
    })
    .map(month => [
      month.name,
      formatCurrency(month.amount),
      month.count.toString()
    ]);
  
  doc.addPage();
  doc.setFontSize(16);
  doc.text('Monthly Revenue Breakdown', 20, 20);
  
  doc.autoTable({
    startY: 30,
    head: [['Month', 'Revenue', 'Payment Count']],
    body: monthlyTableData,
    theme: 'grid',
    headStyles: { fillColor: [66, 139, 202] },
    styles: { fontSize: 10 },
    columnStyles: {
      1: { halign: 'right' } // Revenue column right-aligned
    }
  });
  
  addPageFooter(doc, user, generatedAt);
  
  // Save the PDF
  const fileName = `financial-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fileName);
  
  return { success: true, fileName };
}; 